/* eslint-disable @typescript-eslint/no-empty-function */
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import clock from '../../../../assets/images/icons/clock.svg';
import coins from '../../../../assets/images/icons/coins.svg';
import distance from '../../../../assets/images/icons/distance.svg';
import useApiCall from '../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import ConsultsService from '../../../../services/ConsultsService';
import floatToCurrency from '../../../../utils/floatToCurrency';
import FilterRadioButton from '../../../FilterRadioButtons';
import { FilterRadioButtonsContainer } from '../../../FilterRadioButtonsContainer';
import MyModal from '../../../Modal';
import { IGetRouteOptionApiResponse, IGetRouteOptionMrApiResponse, SectionOptionType, TrajetoSelecaoIda, TrajetosIda } from '../../types';
import Route from './components/Route';
import { ButtonContentContainer } from './styles';

interface IChangeGoingOptionModal {
  changeGoingOptionModalShow: boolean;
  otherGoingOptions: TrajetosIda[];
  setChangeGoingOptionModalShow: Dispatch<SetStateAction<boolean>>;
  changeGoingOption: (selectedOption: TrajetosIda) => Promise<void>;
  selectedGoingRoute: TrajetosIda;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  selectedSection: SectionOptionType;
  isMultiRoute: boolean;
}

export default function ChangeGoingOptionModal({
  changeGoingOptionModalShow,
  otherGoingOptions,
  setChangeGoingOptionModalShow,
  changeGoingOption,
  selectedGoingRoute,
  setIsLoading,
  selectedSection,
  isMultiRoute,
}: IChangeGoingOptionModal) {
  const [selectedGoingOption, setSelectedGoingOption] = useState(selectedGoingRoute);
  const [routeDetails, setRouteDetails] = useState<TrajetoSelecaoIda[]>([]);

  const { apiCall } = useApiCall();
  const { userCode, userProfile, companyCode, usersCompanyCode } = useGetGeneralAppData();
  const { consultCode } = useParams();

  const getCorrespondingRoute = useCallback(async () => {
    await apiCall({
      apiToCall: isMultiRoute ? ConsultsService.getRouteOfOptionMr : ConsultsService.getRouteOfOption,
      queryParams: isMultiRoute
        ?
        {
          codUsu: userCode,
          perfilUsu: userProfile,
          consultCode,
          trajeto: selectedGoingOption.trajeto,
          sequenciaTrajeto: selectedGoingOption.sequenciaTrajeto,
          codEmpresa: usersCompanyCode,
        }
        :
        {
          codUsu: userCode,
          perfilUsu: userProfile,
          codEmpresa: companyCode,
          consultCode,
          sentido: selectedGoingOption.sentido,
          trajeto: selectedGoingOption.trajeto,
          rota: selectedGoingOption.rota,
        },
      actionAfterResponse: isMultiRoute
        ?
        (response: IGetRouteOptionMrApiResponse) => {
          if (!response.codigo) {
            return;
          }
          setRouteDetails(response.trajetoSel?.map((x) => ({ ...x, sentido: x.sentido !== 2 ? 'IDA' : 'VOLTA' })) || []);
        }
        :
        (response: IGetRouteOptionApiResponse) => {
          if (!response.codigo) {
            return;
          }
          setRouteDetails(response.trajetoSelecao);
        },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
    });
  }, [apiCall, companyCode, consultCode, isMultiRoute, selectedGoingOption?.rota, selectedGoingOption?.sentido, selectedGoingOption?.sequenciaTrajeto, selectedGoingOption?.trajeto, setIsLoading, userCode, userProfile, usersCompanyCode]);

  const filteredGoingOptions = otherGoingOptions.filter((rt) => !rt.sequenciaTrajeto || rt.sequenciaTrajeto === selectedSection?.value);

  useEffect(() => {
    if (!changeGoingOptionModalShow) {
      return;
    }
    getCorrespondingRoute();
  }, [getCorrespondingRoute, changeGoingOptionModalShow]);

  useEffect(() => {
    setSelectedGoingOption(selectedGoingRoute);
  }, [selectedGoingRoute]);

  return (
    <MyModal
      show={changeGoingOptionModalShow}
      title={isMultiRoute ? `Opções de rota - Trecho ${selectedSection?.value}` : 'Opções de rota (sentido ida - Casa -> Trabalho)'}
      centeredBody
      modalBody={(
        <>
          <h6 style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '16px' }}>Selecione e confirme a rota desejada</h6>
          <FilterRadioButtonsContainer>
            {filteredGoingOptions.map((rt) => (
              <FilterRadioButton
                key={rt.trajeto}
                selected={rt.trajeto === selectedGoingOption.trajeto}
                onClick={() => setSelectedGoingOption(rt)}
                height="auto"
              >
                <ButtonContentContainer>
                  <h6>Opção {rt.trajeto}</h6>
                  <div>
                    <div>
                      <img src={distance} alt="" className='oppositeTheme' />
                      <span>{rt.distanciaTotTj} m</span>
                    </div>
                    <div>
                      <img src={clock} alt="" className='oppositeTheme' />
                      <span>{rt.tempoTotTj} min</span>
                    </div>
                    <div>
                      <img src={coins} alt="" className='oppositeTheme' />
                      <span>{floatToCurrency(rt.valorVtTotal)}</span>
                    </div>
                  </div>
                </ButtonContentContainer>
              </FilterRadioButton>
            ))}
          </FilterRadioButtonsContainer>

          <Route goingRoute={routeDetails} />
        </>
      )}
      type="action"
      closeButtonLabel="Fechar"
      actionButtonLabel="Confirmar rota"
      onClose={() => {
        setChangeGoingOptionModalShow(false);
        setSelectedGoingOption(selectedGoingRoute);
      }}
      onAction={() => changeGoingOption(selectedGoingOption)}
      isActionButtonDisabled={selectedGoingRoute?.trajeto === selectedGoingOption?.trajeto}
    />
  );
}
